import clsx from 'clsx'
import { FC, useState } from 'react'
import { toAbsoluteUrl } from '_metronic/helpers'
import { getEmpresaSlugFromLoginPath } from 'app/modules/auth/core/loginCompanyConfig'
import { useBrandSplash } from 'app/modules/brand-management/core/brandSplash'
import { getReadableForeground, parseHexColor } from 'app/modules/brand-management/core/color'

const LAUDUZ_LOGO_BY_THEME: Record<'light' | 'dark', string> = {
  light: '/media/logos/logo-white.png',
  dark: '/media/logos/logo-dark.png',
}

const isSafeLogoUrl = (url?: string): url is string => !!url && /^(https?:\/\/|\/)/i.test(url)

type SplashLogo = { src: string; alt: string; isBrand: boolean }

/**
 * Tela de carregamento global, exibida pelo `MetronicSplashScreenProvider` enquanto houver
 * algum `<LayoutSplashScreen />` montado.
 *
 * - Marca conhecida (desta sessão ou salva da anterior): logo da empresa (se falhar, cai no
 *   LauduzOne).
 * - Sem marca, no login de empresa (`/auth/:slug/login`): neutra, sem logo — a marca ainda não é
 *   conhecida e mostrar o LauduzOne ali quebraria o white-label.
 * - Sem marca, nas demais rotas: logo LauduzOne.
 *
 * Fica fora do Router e do i18n (cobre o carregamento deles), por isso lê `window.location`
 * e usa texto fixo.
 */
export const BrandSplashScreen: FC = () => {
  const brand = useBrandSplash()
  const [failedLogoUrl, setFailedLogoUrl] = useState<string>()

  const theme = document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light'
  const isCompanyLogin = !!getEmpresaSlugFromLoginPath(window.location.pathname)
  const brandLogoUrl = brand?.logoUrl

  let logo: SplashLogo | undefined
  if (isSafeLogoUrl(brandLogoUrl) && brandLogoUrl !== failedLogoUrl) {
    logo = { src: brandLogoUrl, alt: `${brand?.fantasia || 'Instituição'} logo`, isBrand: true }
  } else if (!isCompanyLogin) {
    logo = {
      src: toAbsoluteUrl(LAUDUZ_LOGO_BY_THEME[theme]),
      alt: 'LauduzOne logo',
      isBrand: false,
    }
  }

  // Fundo da marca só junto com o logo da marca: se o logo falhou e caiu no LauduzOne, o fundo
  // volta ao do tema (o logo padrão foi feito para ele).
  const brandBackground = logo?.isBrand ? parseHexColor(brand?.backgroundColor) : undefined
  const colors = brandBackground
    ? { backgroundColor: brand?.backgroundColor, color: getReadableForeground(brandBackground) }
    : undefined

  return (
    <div
      className={clsx(
        'position-fixed top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-8 animate__animated animate__fadeIn animate__faster',
        !colors && 'bg-body'
      )}
      style={{ zIndex: 1100, ...colors }}
      role='status'
      aria-live='polite'
    >
      {logo && (
        <img
          src={logo.src}
          alt={logo.alt}
          style={{ height: 40, maxWidth: '80vw', objectFit: 'contain' }}
          onError={logo.isBrand ? () => setFailedLogoUrl(logo?.src) : undefined}
        />
      )}
      <div className='d-flex align-items-center gap-3'>
        {/* Com fundo da marca, herdam a cor de contraste calculada (spinner usa currentColor) */}
        <span
          className={clsx('spinner-border spinner-border-sm', !colors && 'text-primary')}
          aria-hidden='true'
        />
        <span className={clsx('fs-6 fw-semibold', !colors && 'text-gray-700')}>Carregando...</span>
      </div>
    </div>
  )
}
